import React, { HTMLAttributes, useCallback, useMemo, useRef } from "react";
import { IEditorPlugin } from "../../Base/types";
import { EditableProps } from "./Editable";
import Editable from "./Editable";
import { ReactEditor, useSlateStatic } from "slate-react";
import {
  ISuggestEditor,
  QueryChangeEventHandler,
  SuggestEditor,
} from "./SuggestEditor";
import EmptySuggestionItem from "@/components/legacy/menus/EmptySuggestionItem";
import { useCombobox } from "downshift";
import { IAbstractSuggestion } from "@/components/legacy/suggestions/types";
import useHighlightTargetDecorator from "./useHighlightTargetDecorator";
import { is } from "superstruct";
import ScrollableMenuList from "@/components/legacy/menus/ScrollableMenuList";
import useFloatingSuggestionList from "./useFloatingSuggestionList";
import classNames from "classnames";
import { Portal } from "@/components/base/Portal";
import { mergeRefs } from "react-merge-refs";
import VSCodingMenuItem, {
  getKey,
} from "@/components/legacy/menus/VSCodingMenuItem";
import { codingToConcept } from "@/data-models/value-models/types";
import useValueSetValidator from "@/services/content/useValueSetValidator";
import { CodingModel } from "@/data-models/value-models/structs";
import client from "@/services/content/client";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Transition } from "@headlessui/react";
import { useQuery } from "react-query";
import { createValueSetExpansionQuery } from "@/services/content/useValueSetExpansion";

export interface SuggestEditableProps
  extends Omit<EditableProps, "onSubmit">,
    Pick<HTMLAttributes<HTMLDivElement>, "className"> {
  plugins: IEditorPlugin[];
  valueSet?: string;
  onQueryChange?: QueryChangeEventHandler;
  onSubmit?: (suggestion: IAbstractSuggestion) => void;
  editableClassname?: string;
  editableHeader?: React.ReactNode;
  editableFooter?: React.ReactNode;
}
const DEFAULT_VALUESET =
  "http://tiro.health/fhir/ValueSet/snomed-ct-auto-generated";
function SuggestEditable({
  className,
  editableRef,
  editableClassname,
  plugins,
  valueSet: providedValueSet = DEFAULT_VALUESET,
  editableHeader,
  editableFooter,
  onQueryChange: providedHandleQueryChange,
  onSubmit: providedOnSuggestionSubmit,
  onKeyDown: providedOnKeyDown,
  onInput: providedHandleInput,
  onBlur: providedHandleBlur,
  onMouseUp: providedHandleMouseUp,
  onClick: providedHandleClick,
  ...props
}: SuggestEditableProps) {
  const ulRef = useRef<HTMLUListElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const editor = useSlateStatic() as ISuggestEditor;
  const { query } = editor;
  const {
    data: suggestions = [],
    status,
    isError,
  } = useQuery({
    ...createValueSetExpansionQuery(providedValueSet, {
      filter: query?.text,
    }),
    enabled: !!query,
  });
  const { mutate: validate } = useValueSetValidator(providedValueSet);

  const {
    getComboboxProps,
    getInputProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    openMenu,
    closeMenu,
    isOpen,
    highlightedIndex,
  } = useCombobox({
    items: suggestions ?? [],
    defaultHighlightedIndex: 0,
    circularNavigation: true,
    inputValue: editor.query?.text ?? "",
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            selectedItem: null,
            isOpen: false,
          };
        default:
          return {
            ...changes,
            isOpen: ReactEditor.isFocused(editor) && changes.isOpen,
          };
      }
    },
    onSelectedItemChange: async ({ selectedItem }) => {
      if (!!selectedItem && !!editor.query) {
        SuggestEditor.insertSuggestion(
          editor,
          editor.query,
          {
            length: editor.query.text.length,
            offset: 0,
          },
          codingToConcept(selectedItem),
        );
        if (is(selectedItem, CodingModel)) {
          validate(selectedItem);
          await client.events.createEventV2({
            requestBody: {
              valueSet: providedValueSet,
              coding: selectedItem,
              type: "USER_PICKED_CODE",
            },
          });
        }
      }
    },
  });

  const handleQueryChange: QueryChangeEventHandler = useCallback(
    (query) => {
      if (query) {
        openMenu();
      }
      if (!query) {
        closeMenu();
      }

      providedHandleQueryChange && providedHandleQueryChange(query);
    },
    [providedHandleQueryChange, openMenu, closeMenu],
  );

  const highlightedSuggestion = suggestions[highlightedIndex];

  //  we could make this part of custom useCombobox hook
  /**
   * AVR 20220615
   * Notice that this hook is responsible for rerendering the whole component when the query updates
   * It's maybe a little to implicit. How to make the rerender logic more clearly?
   */
  const { floating, x, y, strategy } = useFloatingSuggestionList();

  // decorate the target text that will be replaced with an underlining
  const highlightTargetDecorator = useHighlightTargetDecorator(
    editor.query,
    highlightedSuggestion?.display
      ? { offset: 0, length: highlightedSuggestion?.display?.length }
      : undefined,
  );

  const items = !suggestions || suggestions.length > 0 ? suggestions : null;
  const hide = !items;

  /** Memoize props from downshift menu helper function
   * Mounting the floating element triggers a rerender. We have to make sure that during this rerender the
   * floating props don't change because this would create a endless rerender loop
   */
  const { ref: menuRef, ...menuProps } = useMemo(
    () =>
      getMenuProps({
        ref: mergeRefs([floating, ulRef]), // merge `floating` with urRef; note that `floating` a function ref and ulRef is an object ref
        className: classNames(
          { hidden: hide, visible: !hide },
          "absolute z-50 !not-prose",
        ),
        style: {
          position: strategy,
          top: y ?? "",
          left: x ?? "",
        },
      }),
    [strategy, x, y, ulRef, hide, floating, getMenuProps],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const { currentTarget, relatedTarget } = event;
      const relatedTargetIsNull = relatedTarget === null;
      const relatedTargetIsInsideCombobox =
        currentTarget.contains(relatedTarget);
      const relatedTargetIsInsideMenu =
        ulRef.current && ulRef.current.contains(relatedTarget);
      const relatedTargetIsOutside = !(
        relatedTargetIsInsideCombobox || relatedTargetIsInsideMenu
      );

      if (relatedTargetIsNull || relatedTargetIsOutside) {
        providedHandleBlur && providedHandleBlur(event);
      }
    },
    [providedHandleBlur],
  );

  return (
    <div
      {...getComboboxProps({
        className: classNames("relative", className),
        onBlur: handleBlur,
      })}
    >
      {editableHeader}
      <Editable
        plugins={plugins}
        className={editableClassname}
        {...getInputProps({
          refKey: "editableRef",
          ref: editableRef,
          onClick: providedHandleClick,
          onMouseUp: providedHandleMouseUp,
          onKeyDown: providedOnKeyDown,
          onInput: providedHandleInput,
        })}
        {...props}
        onQueryChange={handleQueryChange}
        decorate={isOpen ? highlightTargetDecorator : undefined}
        // THIS PREVENTS THE EDITOR FROM SCROLLING TO THE BOTTOM
        // WHEN THE USER CLICKS ON BLOCK VOID ELEMENTS
        scrollSelectionIntoView={() => undefined}
      />
      <button
        type="button"
        className="sr-only"
        {...getToggleButtonProps({ tabIndex: -1, ref: toggleRef })}
      />
      <Transition
        as="div"
        show={isError}
        className="absolute bottom-0 right-0 flex items-center justify-center text-xs text-red-500"
      >
        <ExclamationTriangleIcon className="mr-1 h-4 w-4" />
      </Transition>
      {editableFooter}
      <Portal>
        <ScrollableMenuList ref={menuRef} open={isOpen} {...menuProps}>
          {/** iterate through all items and render them per type */}
          {items &&
            items.map((item, index) => (
              <VSCodingMenuItem
                key={getKey(item)}
                {...getItemProps({
                  item,
                  index,
                  key: item.code,
                  tabIndex: 0,
                })}
                item={item}
                active={index === highlightedIndex}
              />
            ))}
          {/** if there are no items */}
          {!items && <EmptySuggestionItem status={status} />}
        </ScrollableMenuList>
      </Portal>
    </div>
  );
}
export default SuggestEditable;
